// Navigation rail component for document and collection pages.

import React, { useEffect, useState } from 'react';
import { ArrowUpIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import type { OutlineItem } from '@/content/lib/outline';
import { Button } from '@/components/ui/button';
import TableOfContents from '@/content/rendering/chrome/TableOfContents';

interface NavigationRailProps {
  outline: OutlineItem[];
}

const BACK_TO_TOP_THRESHOLD = 480;

const BackToTopButton: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const updateVisibility = () => {
      setVisible(window.scrollY > BACK_TO_TOP_THRESHOLD);
    };

    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });

    return () => {
      window.removeEventListener('scroll', updateVisibility);
    };
  }, []);

  const handleClick = () => {
    if (location.hash) {
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: '',
        },
        { replace: true }
      );
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  if (!visible) {
    return null;
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="fixed right-4 bottom-4 z-50 bg-background shadow-sm sm:right-6 sm:bottom-6 lg:hidden"
      onClick={handleClick}
      aria-label="Back to top"
    >
      <ArrowUpIcon data-icon="inline-start" aria-hidden="true" />
      Top
    </Button>
  );
};

const NavigationRail: React.FC<NavigationRailProps> = ({ outline }) => {
  if (outline.length === 0) {
    return null;
  }

  return (
    <>
      <aside className="border-b border-border bg-background/70 pb-3 lg:sticky lg:top-28 lg:max-h-[calc(100svh-8rem)] lg:overflow-auto lg:border-r lg:border-b-0 lg:pr-4 lg:pb-0">
        <TableOfContents items={outline} />
      </aside>
      <BackToTopButton />
    </>
  );
};

export default NavigationRail;
